/**
 * MonteCarloJurisdictionsPage — MC × Jurisdictions retirement success comparison
 *
 * The headline insight: "If I want £135k NET (≈ £220k gross in the UK), what
 * gross withdrawal do I need in each jurisdiction, and what is the resulting
 * Monte Carlo success rate?"
 *
 * NOTE: this page's target is NET (after tax) — it deliberately does NOT
 * hydrate from or write back to the shared GROSS `target_drawdown_gbp`
 * assumption, because net ≠ gross.
 *
 * Higher-tax jurisdictions require more gross, drain the pot faster, and produce
 * lower success probabilities. Low / zero tax jurisdictions produce the inverse.
 */
import React, { useEffect, useState } from 'react'
import { Globe, Trophy, RefreshCw, Info, Sparkles } from 'lucide-react'
import { clsx } from 'clsx'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList,
} from 'recharts'
import { api } from '@/lib/api'
import ScenariosTabs from '@/components/ui/ScenariosTabs'
import EditableNumberSlider from '@/components/ui/EditableNumberSlider'

// ── Types ────────────────────────────────────────────────────────────────────

interface TaxBandBreakdown {
  label: string
  rate_pct: number
  income_in_band_gbp: number
  tax_in_band_gbp: number
}

interface JurisdictionMCResult {
  jurisdiction_id: string
  name: string
  flag: string
  gross_needed_gbp: number
  annual_tax_gbp: number
  effective_tax_rate_pct: number
  success_probability_pct: number
  median_final_gbp: number
  p10_final_gbp: number
  depletion_age_median: number | null
  delta_vs_uk_pp: number
  // Calculation narrative + breakdown (new)
  calc_narrative?: string | null
  calc_breakdown?: TaxBandBreakdown[] | null
  per_person_gross_gbp?: number
  per_person_net_gbp?: number
  per_person_tax_gbp?: number
  n_people?: number
}

interface CrossResponse {
  target_net_gbp: number
  joint: boolean
  num_simulations: number
  household_gbp: number
  results: JurisdictionMCResult[]
  uk_baseline_success_pct: number
  winner_id: string
  headline: string
}

// ── Formatting helpers ────────────────────────────────────────────────────────

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtCompact = (n: number) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

const fmtPct = (n: number) => `${n.toFixed(1)}%`

// ── Bar colour by success probability ────────────────────────────────────────

function barColour(pct: number): string {
  if (pct >= 90) return '#10b981'  // emerald
  if (pct >= 70) return '#f59e0b'  // amber
  return '#f43f5e'                 // rose
}

function successTextClass(pct: number): string {
  if (pct >= 90) return 'text-emerald-light'
  if (pct >= 70) return 'text-amber'
  return 'text-rose-light'
}

function successBadgeClass(pct: number): string {
  if (pct >= 90) return 'badge-green'
  if (pct >= 70) return 'badge-amber'
  return 'badge-red'
}

// ── Slider ───────────────────────────────────────────────────────────────────

function NumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  format,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="panel-title">{label}</label>
        <span className="text-xs font-bold text-text-primary">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
      />
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MonteCarloJurisdictionsPage() {
  // NET target (after tax) — local only, sent as `target_net_gbp`.
  // £135k net ≈ £220k gross under UK tax; do not sync with target_drawdown_gbp.
  const [targetNet, setTargetNet] = useState(135_000)
  const [volatility, setVolatility] = useState(12)
  const [meanReturn, setMeanReturn] = useState(5.0)
  const [numSims, setNumSims] = useState(5_000)

  const [data, setData] = useState<CrossResponse | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const runSimulation = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.post<CrossResponse>('/api/planning/monte-carlo-jurisdictions', {
        target_net_gbp: targetNet,
        joint: true,
        current_age: 57,
        retirement_age: 60,
        age_target: 95,
        mean_real_return_pct: meanReturn,
        volatility_pct: volatility,
        num_simulations: numSims,
      })
      setData(res.data)
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? 'Simulation failed')
    } finally {
      setLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    runSimulation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced re-run on input change
  useEffect(() => {
    const t = setTimeout(runSimulation, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetNet, volatility, meanReturn, numSims])

  // Derived
  const winner = data?.results[0] ?? null
  const ukResult = data?.results.find(r => r.jurisdiction_id === 'UK') ?? null

  // Chart data (already sorted DESC by success_probability from backend)
  const chartData = (data?.results ?? []).map(r => ({
    label: `${r.flag} ${r.jurisdiction_id}`,
    success: r.success_probability_pct,
    name: r.name,
    gross: r.gross_needed_gbp,
    tax: r.annual_tax_gbp,
    rate: r.effective_tax_rate_pct,
    delta: r.delta_vs_uk_pp,
  })) ?? []

  // Loading skeleton
  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-8 w-80 skeleton" />
        <div className="h-24 skeleton" />
        <div className="h-72 skeleton" />
        <div className="h-48 skeleton" />
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error}</p>
        <button type="button" onClick={runSimulation} className="btn-secondary mt-3">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Globe size={20} className="text-blue-light" aria-hidden />
            Monte Carlo × Jurisdictions
          </h1>
          <p className="text-sm text-text-secondary">
            Same net lifestyle, different tax jurisdiction.
            Which one survives {(numSims / 1000).toFixed(0)}k simulated futures?
          </p>
        </div>
        <button
          type="button"
          onClick={runSimulation}
          disabled={loading}
          className="btn-secondary gap-2 text-xs"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          {loading ? 'Simulating…' : 'Re-run'}
        </button>
      </div>

      <ScenariosTabs />

      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-rose-light text-sm">
          {error}
        </div>
      )}

      {/* ── Inputs ─────────────────────────────────────────────────────────── */}
      <div className="card p-5">
        <h2 className="panel-title mb-4 flex items-center gap-2">
          <Sparkles size={14} className="text-blue-light" aria-hidden />
          Simulation Inputs
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <EditableNumberSlider
            label="Target net income / yr (after tax)"
            value={targetNet}
            onChange={setTargetNet}
            min={50_000}
            max={400_000}
            step={5_000}
            format={fmt}
            caption={targetNet === 135_000 ? '≈ £220k gross' : undefined}
          />
          <NumberSlider
            label="Return volatility (σ)"
            value={volatility}
            onChange={setVolatility}
            min={5}
            max={20}
            step={1}
            format={(v) => `${v}%`}
          />
          <NumberSlider
            label="Mean real return"
            value={meanReturn}
            onChange={setMeanReturn}
            min={1}
            max={10}
            step={0.5}
            format={(v) => `${v.toFixed(1)}%`}
          />
          <NumberSlider
            label="Simulations"
            value={numSims}
            onChange={setNumSims}
            min={1_000}
            max={20_000}
            step={1_000}
            format={(v) => v.toLocaleString()}
          />
        </div>
      </div>

      {/* ── Headline callout ────────────────────────────────────────────────── */}
      {data && winner && (
        <div className={clsx(
          'card p-5 border-2',
          winner.success_probability_pct >= 90
            ? 'border-emerald/40 bg-emerald/5'
            : winner.success_probability_pct >= 70
              ? 'border-amber/40 bg-amber/5'
              : 'border-rose/40 bg-rose/5',
        )}>
          <p className="panel-title">
            Monte Carlo Result — {(data.num_simulations ?? 0).toLocaleString()} simulations ·
            {fmt(data.target_net_gbp)} net target
          </p>
          <p className="text-lg font-bold text-text-primary mt-1">{data.headline}</p>
          <p className="text-xs text-text-muted mt-2">
            Starting from {fmtCompact(data.household_gbp)} FIRE-applicable wealth,
            drawing a flat real gross amount until the portfolio reaches zero or age 95.
            Higher-tax jurisdictions require more gross withdrawal, draining the pot faster.
          </p>
        </div>
      )}

      {/* ── KPI strip ───────────────────────────────────────────────────────── */}
      {data && winner && ukResult && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
          <div className="stat-card card-hover border-emerald/30 bg-emerald/5">
            <p className="stat-label flex items-center gap-1">
              <Trophy size={11} className="text-emerald-light" aria-hidden /> Winner
            </p>
            <p className="stat-value text-emerald-light">
              {winner.flag} {winner.name}
            </p>
            <p className="text-xs text-text-muted">
              {(winner.success_probability_pct ?? 0).toFixed(0)}% success — gross {fmt(winner.gross_needed_gbp)}/yr
            </p>
          </div>

          <div className="stat-card border-rose/20 bg-rose/5">
            <p className="stat-label">UK Baseline</p>
            <p className={clsx('stat-value', successTextClass(ukResult.success_probability_pct))}>
              {(ukResult.success_probability_pct ?? 0).toFixed(0)}% success
            </p>
            <p className="text-xs text-text-muted">
              Needs {fmt(ukResult.gross_needed_gbp)}/yr gross · {fmtPct(ukResult.effective_tax_rate_pct)} tax
            </p>
          </div>

          <div className="stat-card">
            <p className="stat-label">Best improvement</p>
            <p className="stat-hero text-blue-light">
              +{(winner.delta_vs_uk_pp ?? 0).toFixed(0)}pp
            </p>
            <p className="text-xs text-text-muted">
              {winner.flag} {winner.name} vs UK baseline
            </p>
          </div>

          <div className="stat-card">
            <p className="stat-label">UK gross needed</p>
            <p className="stat-value text-rose-light">{fmt(ukResult.gross_needed_gbp)}/yr</p>
            <p className="text-xs text-text-muted">
              vs {fmt(winner.gross_needed_gbp)}/yr in {winner.name} — saves {fmt(ukResult.gross_needed_gbp - winner.gross_needed_gbp)}/yr
            </p>
          </div>
        </div>
      )}

      {/* ── Bar chart ───────────────────────────────────────────────────────── */}
      {chartData.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-1 flex items-center gap-2">
            <Globe size={14} className="text-blue-light" aria-hidden />
            Success Probability by Jurisdiction
          </h2>
          <p className="text-xs text-text-muted mb-4">
            Each bar shows the Monte Carlo success probability for delivering {fmt(targetNet)} net/yr
            to age 95 — sorted best to worst. Green ≥90%, amber 70-90%, rose &lt;70%.
          </p>

          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 50, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 100]}
                stroke="#94a3b8"
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}%`}
              />
              <YAxis
                type="category"
                dataKey="label"
                stroke="#94a3b8"
                tick={{ fontSize: 12 }}
                width={60}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgb(var(--bg-surface))', border: '1px solid rgb(var(--border))', color: 'rgb(var(--text-primary))',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value: any, _name: string, props: any) => {
                  const item = props.payload
                  return [
                    `${(value as number).toFixed(1)}% success`,
                    `${item.name} — gross ${fmt(item.gross)}, tax ${fmt(item.tax)} (${(item.rate ?? 0).toFixed(1)}%)`,
                  ]
                }}
                labelFormatter={(l) => l as string}
              />
              <Bar dataKey="success" radius={[0, 4, 4, 0]} maxBarSize={32}>
                {chartData.map((entry, idx) => (
                  <Cell key={idx} fill={barColour(entry.success)} />
                ))}
                <LabelList
                  dataKey="success"
                  position="right"
                  formatter={(v: any) => `${(v as number).toFixed(0)}%`}
                  style={{ fill: '#94a3b8', fontSize: 11 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Detail table ─────────────────────────────────────────────────────── */}
      {data && data.results.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="panel-title">Detailed Breakdown</h2>
            <span className="text-xs text-text-muted">sorted by success probability</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs table-pro">
              <thead>
                <tr>
                  <th className="px-4">Jurisdiction</th>
                  <th className="!text-right">Gross Needed</th>
                  <th className="!text-right">Annual Tax</th>
                  <th className="!text-right hidden sm:table-cell">
                    Eff. Rate
                  </th>
                  <th className="!text-right">
                    Success %
                  </th>
                  <th className="!text-right hidden md:table-cell">
                    Median Final
                  </th>
                  <th className="!text-right px-4">
                    vs UK
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((row, i) => {
                  const isWinner = i === 0
                  const isUK = row.jurisdiction_id === 'UK'
                  const isExpanded = expandedId === row.jurisdiction_id
                  return (
                    <React.Fragment key={row.jurisdiction_id}>
                    <tr
                      onClick={() => setExpandedId(isExpanded ? null : row.jurisdiction_id)}
                      className={clsx(
                        'transition-colors cursor-pointer',
                        isWinner && 'bg-emerald/10 hover:bg-emerald/15',
                        isUK && !isWinner && 'bg-rose/5 hover:bg-rose/10',
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg" role="img" aria-label={row.name}>
                            {row.flag}
                          </span>
                          <div>
                            <p className={clsx(
                              'font-semibold',
                              isWinner ? 'text-emerald-light' : 'text-text-primary',
                            )}>
                              {row.name}
                            </p>
                            {isWinner && (
                              <span className="badge badge-green text-[10px]">WINNER</span>
                            )}
                            {isUK && !isWinner && (
                              <span className="badge badge-red text-[10px]">BASELINE</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-right px-3 py-3 font-mono text-text-primary">
                        {fmt(row.gross_needed_gbp)}
                      </td>
                      <td className="text-right px-3 py-3 font-mono text-rose-light">
                        {row.annual_tax_gbp > 0 ? fmt(row.annual_tax_gbp) : (
                          <span className="text-emerald-light">£0</span>
                        )}
                      </td>
                      <td className="text-right px-3 py-3 font-mono text-text-secondary hidden sm:table-cell">
                        {fmtPct(row.effective_tax_rate_pct)}
                      </td>
                      <td className="text-right px-3 py-3">
                        <span className={clsx('font-bold', successTextClass(row.success_probability_pct))}>
                          {(row.success_probability_pct ?? 0).toFixed(1)}%
                        </span>
                        {' '}
                        <span className={clsx('badge text-[10px]', successBadgeClass(row.success_probability_pct))}>
                          {row.success_probability_pct >= 90 ? 'STRONG' : row.success_probability_pct >= 70 ? 'OK' : 'RISK'}
                        </span>
                      </td>
                      <td className="text-right px-3 py-3 font-mono text-text-secondary hidden md:table-cell">
                        {fmtCompact(row.median_final_gbp)}
                      </td>
                      <td className="text-right px-4 py-3 font-mono font-bold">
                        {row.delta_vs_uk_pp > 0 ? (
                          <span className="text-emerald-light">+{(row.delta_vs_uk_pp ?? 0).toFixed(1)}pp</span>
                        ) : row.delta_vs_uk_pp < 0 ? (
                          <span className="text-rose-light">{(row.delta_vs_uk_pp ?? 0).toFixed(1)}pp</span>
                        ) : (
                          <span className="text-text-muted">baseline</span>
                        )}
                      </td>
                    </tr>

                    {isExpanded && (row.calc_narrative || row.calc_breakdown) && (
                      <tr className="bg-surface-card/40">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="space-y-3">
                            <div className="flex items-start gap-2">
                              <span className="text-lg">{row.flag}</span>
                              <div className="flex-1">
                                <p className="panel-title mb-1">
                                  How the numbers were calculated — {row.name}
                                </p>
                                {row.calc_narrative && (
                                  <p className="text-sm text-text-secondary leading-relaxed">
                                    {row.calc_narrative}
                                  </p>
                                )}
                              </div>
                            </div>

                            {row.calc_breakdown && row.calc_breakdown.length > 0 && (
                              <div className="bg-surface rounded-lg border border-border overflow-hidden">
                                <div className="grid grid-cols-4 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-text-muted font-semibold bg-surface-hover/30">
                                  <div>Band</div>
                                  <div className="text-right">Rate</div>
                                  <div className="text-right">Income in band</div>
                                  <div className="text-right">Tax</div>
                                </div>
                                {row.calc_breakdown.map((b, bi) => (
                                  <div
                                    key={bi}
                                    className="grid grid-cols-4 gap-2 px-3 py-1.5 text-xs border-t border-border/40"
                                  >
                                    <div className="text-text-primary">{b.label}</div>
                                    <div className="text-right font-mono text-text-secondary">
                                      {(b.rate_pct ?? 0).toFixed(1)}%
                                    </div>
                                    <div className="text-right font-mono text-text-secondary">
                                      {fmt(b.income_in_band_gbp)}
                                    </div>
                                    <div className="text-right font-mono text-rose-light">
                                      {fmt(b.tax_in_band_gbp)}
                                    </div>
                                  </div>
                                ))}
                                <div className="grid grid-cols-4 gap-2 px-3 py-1.5 text-xs border-t-2 border-border font-semibold bg-surface-hover/20">
                                  <div className="text-text-primary">Total</div>
                                  <div className="text-right font-mono text-text-secondary">
                                    {(row.effective_tax_rate_pct ?? 0).toFixed(1)}%
                                  </div>
                                  <div className="text-right font-mono text-text-secondary">
                                    {fmt(row.per_person_gross_gbp ?? row.gross_needed_gbp / (row.n_people || 1))} / person
                                  </div>
                                  <div className="text-right font-mono text-rose-light">
                                    {fmt(row.per_person_tax_gbp ?? row.annual_tax_gbp / (row.n_people || 1))} / person
                                  </div>
                                </div>
                              </div>
                            )}

                            {row.n_people && row.n_people > 1 && (
                              <div className="flex flex-wrap gap-4 text-xs text-text-secondary border-t border-border pt-2">
                                <span>
                                  <strong>Joint couple:</strong>
                                </span>
                                <span>Per person gross: <span className="font-mono text-text-primary">{fmt(row.per_person_gross_gbp ?? 0)}</span></span>
                                <span>Per person tax: <span className="font-mono text-rose-light">{fmt(row.per_person_tax_gbp ?? 0)}</span></span>
                                <span>Per person net: <span className="font-mono text-emerald-light">{fmt(row.per_person_net_gbp ?? 0)}</span></span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-[11px] text-text-muted border-t border-border">
            Joint household · ages 57→60 retire → 95 horizon ·{' '}
            {(data.num_simulations ?? 0).toLocaleString()} simulations ·{' '}
            {fmt(data.household_gbp)} starting pot
          </p>
        </div>
      )}

      {/* ── Methodology footer ───────────────────────────────────────────────── */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold flex items-center gap-2">
          <Info size={13} aria-hidden />
          Methodology — How the gross-up works
        </summary>
        <div className="mt-3 space-y-2 leading-relaxed">
          <p>
            <span className="text-text-primary font-semibold">Target NET approach:</span>{' '}
            Rather than fixing the gross withdrawal and comparing take-home, this simulation fixes
            the target NET lifestyle (the slider above) and solves backwards for each jurisdiction's
            required gross via bisection. The gross is what actually leaves the pot each year.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Joint household:</span>{' '}
            The gross is split equally between two people. Each person's tax is computed
            independently on their half, then doubled for the household total.
            This reflects the benefit of income-splitting in joint retirement.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Monte Carlo engine:</span>{' '}
            Each path draws annual real returns from Normal(mean, σ). Spending is flat in real terms
            p.a. from retirement. A path "fails" the moment the portfolio hits zero.
            Success rate = fraction of paths still positive at the target age.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Tax functions used:</span>{' '}
            UK progressive (45% additional rate + 60% PA trap), HK lower-of-progressive/15%,
            SG progressive 0-24%, Cyprus 5% flat foreign pension, Malta 15% flat (min €7,500),
            Macau 0% (pension exempt), Portugal NHR 10% flat (reference — NHR closed 2024).
          </p>
          <p className="text-text-muted">
            Standard caveats: actual returns are not normally distributed; this model ignores
            sequence-of-returns risk amplification, state pensions, currency risk, and cost-of-
            living differences between jurisdictions. Use as directional insight, not financial advice.
          </p>
        </div>
      </details>
    </div>
  )
}
